import { IBuyPackage, IPackageBuyInfo } from '@/Interfaces';
import { IValidator, Validations } from '@samc/centricity-core';
import { PackageValidator } from './PackageValidator';
import { PackageBuyInfoValidator } from './PackageBuyInfoValidator';

export class BuyPackageModalValidator implements IValidator<IBuyPackage> {
  public isValid(value: IBuyPackage): Validations<IBuyPackage> {
    const validations: Validations<IBuyPackage> = {};

    if (value.status !== 'Bid') validations.status = 'Status cannot be changed!';
    if (value.name.length === 0) validations.name = 'Package Name is Required!';
    if (!value.packageBuyInfo.bidRequestDate) validations.packageBuyInfo = { ...(validations.packageBuyInfo as Validations<IPackageBuyInfo>), ...{ bidRequestDate: 'Bid Request Date is Required!' } };
    if (!value.packageBuyInfo.bidDueDate) validations.packageBuyInfo = { ...(validations.packageBuyInfo as Validations<IPackageBuyInfo>), ...{ bidDueDate: 'Bid Due Date is Required!' } };
    if (value.assetClass.id === undefined || value.assetClass.id === null) validations.assetClass = { id: 'Asset is Required!' };
    if (value.packageBuyInfo.balanceType.id.length === 0) validations.packageBuyInfo = { ...(validations.packageBuyInfo as Validations<IPackageBuyInfo>), ...{ balanceType: { id: 'Balance Type is Required!' } } };
    if (value.country.id.length === 0) validations.country = { id: 'Country is Required!' };
    if (value.currency.id.length === 0) validations.currency = { id: 'Currency is Required!' };
    if (!value.packageBuyInfo.includeLoanRights && !value.packageBuyInfo.includePPPRights && !value.packageBuyInfo.includeServicingRights)
      validations.packageBuyInfo = { ...(validations.packageBuyInfo as Validations<IPackageBuyInfo>), ...{ includeLoanRights: 'Strip Type is Required!' } };

    if (value.packageBuyInfo.bidRequestDate && value.packageBuyInfo.bidDueDate && new Date(value.packageBuyInfo.bidRequestDate) > new Date(value.packageBuyInfo.bidDueDate)) {
      validations.packageBuyInfo = { ...(validations.packageBuyInfo as Validations<IPackageBuyInfo>), ...{ bidRequestDate: 'Bid Request Date cannot be after Bid Due Date.' } };
      validations.packageBuyInfo = { ...(validations.packageBuyInfo as Validations<IPackageBuyInfo>), ...{ bidDueDate: 'Bid Due Date cannot be before Bid Request Date.' } };
    }

    return validations;
  }
}

export class BuyPackageValidator implements IValidator<IBuyPackage> {
  private _packageValidator: PackageValidator = new PackageValidator();
  private _packageBuyInfoValidator: PackageBuyInfoValidator = new PackageBuyInfoValidator();

  public isValid(value: IBuyPackage, shouldValidateName?: boolean): Record<string, string> {
    const validations = this._packageValidator.isValid(value, shouldValidateName);
    if (value.packageBuyInfo) {
      const innerValidations = this._packageBuyInfoValidator.isValid(value.packageBuyInfo);
      for (let i in innerValidations) {
        validations[`packageBuyInfo.${i}`] = innerValidations[i];
      }

      // Handle required validations based on status
      if ((value.status === 'Lost' || value.status === 'Passed') && !value.packageBuyInfo.lostDate)
        validations['packageBuyInfo.lostDate'] = this.getRequiredFieldValidationMessage('Lost Date');
      if (value.status === 'Won' && !value.packageBuyInfo.tradeDate)
        validations['packageBuyInfo.tradeDate'] = this.getRequiredFieldValidationMessage('Trade Date');

      //Required Fields should be required for all status updates, 
      //unless the user is reverting the status back to a previous status
      if ((value.status === 'Won' || value.status === 'Pending Settle' || value.status === 'Funded') && !value.packageBuyInfo.originalSettledDate)
        validations['packageBuyInfo.originalSettledDate'] = this.getRequiredFieldValidationMessage('Target Settlement Date');
      if ((value.status === 'Won' || value.status === 'Pending Settle' || value.status === 'Funded') && !value.packageBuyInfo.bidPrice)
        validations['packageBuyInfo.bidPrice'] = this.getRequiredFieldValidationMessage('Bid Price');
      
      // Handle empty validations based on status
      if ((value.status === 'Won' || value.status === 'Lost' || value.status === 'Passed') && value.packageBuyInfo.settledDate)
        validations['packageBuyInfo.settledDate'] = this.getEmptyFieldValidationMessage('Funding Settlement Date');
      if (value.status === 'Won' && value.packageBuyInfo.lostDate)
        validations['packageBuyInfo.lostDate'] = this.getEmptyFieldValidationMessage('Lost Date');
      if ((value.status === 'Won' || value.status === 'Pending Settle' || value.status === 'Funded') && !value.packageBuyInfo.tradeDate)
        validations['packageBuyInfo.tradeDate'] = this.getRequiredFieldValidationMessage('Trade Date');
      if ((value.status === 'Won' || value.status === 'Pending Settle' || value.status === 'Funded') && (!value.packageBuyInfo.buyPrice))
        validations['packageBuyInfo.buyPrice'] = this.getRequiredFieldValidationMessage('Buy Price');
      if ((value.status === 'Won' || value.status === 'Pending Settle' || value.status === 'Funded') && !value.packageBuyInfo.trader)
        validations['packageBuyInfo.trader'] = this.getRequiredFieldValidationMessage('Trader');
      if ((value.status === 'Funded') && !value.packageBuyInfo.settledDate)
        validations['packageBuyInfo.settledDate'] = this.getRequiredFieldValidationMessage('Funding Settlement Date');

      const wonOrAbove = ['Won', 'Pending Settle', 'Funded'];
      if ((wonOrAbove.indexOf(value.status) !== -1) && value.packageBuyInfo.proposedServicerId === null)
        validations['packageBuyInfo.proposedServicerId'] = this.getRequiredFieldValidationMessage('Proposed Servicer');
      if ((wonOrAbove.indexOf(value.status) !== -1) && value.packageBuyInfo.proposedServicerId !== -1 && (value.packageBuyInfo.proposedServicerTransferDate ?? '').length === 0)
        validations['packageBuyInfo.proposedServicerTransferDate'] = this.getRequiredFieldValidationMessage('Proposed Servicing Transfer Date');
      if ((wonOrAbove.indexOf(value.status) !== -1) && value.packageBuyInfo.interimServicerId === null)
        validations['packageBuyInfo.interimServicerId'] = this.getRequiredFieldValidationMessage('Interim Servicer');
      if ((wonOrAbove.indexOf(value.status) !== -1) && value.packageBuyInfo.proposedCustodianId === null)
        validations['packageBuyInfo.proposedCustodianId'] = this.getRequiredFieldValidationMessage('Proposed Custodian');
    }
    return validations;
  }

  private getRequiredFieldValidationMessage(fullFieldName: string): string {
    return `${fullFieldName} is required.`;
  }

  private getEmptyFieldValidationMessage(fullFieldName: string): string {
    return `${fullFieldName} should be blank.`;
  }
}